import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileSignature, Search, Siren, ArrowRight } from "lucide-react";
import { fadeUp, Eyebrow, VIEWPORT } from "./primitives";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Three doors out of the home page. Replaces the full passport preview and the
 * verification tool, which now live on their own pages: the home page shows
 * where to go, the pages do the work.
 */
const Door = ({ to, icon: Icon, title, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group flex flex-col border border-ink/10 bg-surface p-7 transition-colors duration-300 hover:border-gold/40 hover:bg-surface-raised"
  >
    <Icon size={22} className="text-gold-ink mb-5" />
    <h3 className="font-serif text-xl mb-2">{title}</h3>
    <p className="text-sm text-ink-muted leading-relaxed flex-1">{children}</p>
    <ArrowRight
      size={16}
      className="text-gold-ink mt-5 transition-transform duration-300 group-hover:translate-x-1"
    />
  </Link>
);

export const ExploreSection = () => {
  const { t } = useLanguage();

  return (
    <section
      id="explorer"
      data-testid="explore-section"
      className="relative scroll-mt-28 py-20 md:py-28 border-t border-ink/5"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={fadeUp}
          className="mb-10"
        >
          <Eyebrow testId="explore-eyebrow">{t("explore.eyebrow")}</Eyebrow>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={fadeUp}
          className="grid md:grid-cols-3 gap-5"
        >
          <Door
            to="/le-passeport"
            icon={FileSignature}
            title={t("explore.passportTitle")}
            testId="explore-passport"
          >
            {t("explore.passportText")}
          </Door>
          <Door
            to="/verifier"
            icon={Search}
            title={t("explore.verifyTitle")}
            testId="explore-verify"
          >
            {t("explore.verifyText")}
          </Door>
          <Door
            to="/montres-volees"
            icon={Siren}
            title={t("explore.stolenTitle")}
            testId="explore-stolen"
          >
            {t("explore.stolenText")}
          </Door>
        </motion.div>
      </div>
    </section>
  );
};

export default ExploreSection;
